import { Button, Form, Space, Table, TableProps, message } from "antd";
import { useEffect, useState } from "react";
import { BsPencilSquare, BsTrashFill } from "react-icons/bs";
import VocsModal from "src/components/Modal";
import { CommonError, CommonSuccess } from "src/entity/common/menus-enum.enum";
import { EmissionControl, Filter, initEmissionControl } from "src/entity/settings/terminal-market";
import { createEmissionControl, deleteEmissionControl, updateEmissionControl } from "src/services/settings.service";
import { terminalStore } from "src/stores/terminal-store";
import { componentPalette } from "src/theme/component-palette";
import { useShallow } from "zustand/react/shallow";
import EmissionControlModal from "./EmissionControlModal";
import { PlusCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { canEditTable } from "../PermissionCheck/PermissionCheck";
import { formatNumberWithFillZero } from "src/utils/process-number";
import { calculateReportVocEmissionMasterChange } from "src/services/terminal-marketing.service";

type OnChange = NonNullable<TableProps<any>["onChange"]>;
type Filters = Parameters<OnChange>[1];

const EmissionControlTable = () => {
  const emissionControl = terminalStore(useShallow((state) => state.emissionControl));
  const totalEmissionControl = terminalStore(useShallow((state) => state.totalEmissionControl));
  const getEmissionControl = terminalStore(useShallow((state) => state.getEmissionControl));
  const { permissions } = useNavigationMenu();

  const filterEmissionControl = terminalStore(useShallow((state) => state.filterEmissionControl));
  const getFilterEmissionControl = terminalStore(useShallow((state) => state.getFilterEmissionControl));

  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [mode, setMode] = useState<"insert" | "update">("insert");
  const [filter, setFilter] = useState<Filters>({});
  const [showModal, setShowModal] = useState(false);
  const [form] = Form.useForm();
  const [emissionId, setEmissionId] = useState<string>("");
  const [messageApi, contextHolder] = message.useMessage();
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);

  const showDeleteConfirm = async (value: EmissionControl) => {
    const { _id } = value;
    VocsModal.delete({
      onOk: async () => {
        if (_id) {
          handleDelete(_id);
        }
      },
    });
  };

  const onOpenModal = (mode: "insert" | "update", data: EmissionControl) => {
    setMode(mode);
    setShowModal(true);
    form.setFieldsValue(data);
    setEmissionId(data._id);
  };

  const onSave = async (mode: "insert" | "update", data: EmissionControl) => {
    try {
      let response;
      setLoading(true);
      if (mode == "insert") {
        response = await createEmissionControl(data);
      } else {
        if (emissionId) {
          data = {
            ...data,
            _id: emissionId,
          };
          response = await updateEmissionControl(data);
        }
      }
      if (response?.data.code === 200) {
        setLoading(false);
        setShowModal(false);
        messageApi.open({
          type: "success",
          content: CommonSuccess.SAVED_SUCCESSFULL,
        });
        fectchEmissionControl();
        getFilterEmissionControl();
        processCalculateReport(data._id);
      } else {
        setLoading(false);
        setIsDuplicate(false);
        messageApi.open({
          type: "error",
          content: CommonError.SOMETHING_WRONG,
        });
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === 409) {
          setIsDuplicate(true);
        } else {
          setIsDuplicate(false);
        }
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const processCalculateReport = async (emissionControlId: string) => {
    try {
      setLoading(true);
      await calculateReportVocEmissionMasterChange({
        chemicalId: "",
        loadingById: "",
        emissionControlId,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setLoading(true);
      const response = await deleteEmissionControl(id);
      if (response.data.code === 200) {
        setLoading(false);
        messageApi.open({
          type: "success",
          content: CommonSuccess.DELETE_SUCCESSFULL,
        });
        fectchEmissionControl();
      } else {
        setLoading(false);
        messageApi.open({
          type: "error",
          content: CommonError.SOMETHING_WRONG,
        });
      }
    } catch (error) {
      console.error(error);
      messageApi.open({
        type: "error",
        content: CommonError.SOMETHING_WRONG,
      });
    } finally {
      setLoading(false);
    }
  };

  const mapFilter = (key: string): Filter[] | [] => {
    const resultFilter = filterEmissionControl.find((item) => item.key === key)?.filter;
    if (resultFilter) {
      const filter: Filter[] = resultFilter?.map((value: any) => {
        let text = value;
        return {
          text,
          value: value,
        };
      });
      return filter;
    } else {
      return [];
    }
  };

  const calPagination = (current?: number) => {
    if (current) {
      return (current - 1) * 10;
    }
  };

  const createParamFilter = () => {
    let queryParam = "";
    let offset = 0;
    let limit = pageSize;

    if (current) {
      let resultCalOffset = calPagination(current);
      if (resultCalOffset) {
        offset = resultCalOffset;
      }
      queryParam += `&offset=${offset}`;
    }
    if (limit) {
      queryParam += `&limit=${limit}`;
    }

    if (filter) {
      const str = Object.entries(filter)
        .filter(([, value]) => value)
        .map(([key, value]) => `&${key}=[${value?.join(",")}]`)
        .join("");
      queryParam += str;
    }

    return queryParam;
  };

  const fectchEmissionControl = async () => {
    setLoading(true);
    const query = createParamFilter();
    await getEmissionControl(query);
    setLoading(false);
  };

  const onTableChange: TableProps<EmissionControl>["onChange"] = (pagination: any, filters: any) => {
    let current = pagination.current;
    setFilter(filters);
    setCurrent(current);
  };

  const onCloseModal = () => {
    setShowModal(false);
    setIsDuplicate(false);
    const formValue = form.getFieldsValue();
    if (formValue) {
      form.setFields(
        Object.keys(formValue).map((field) => ({
          name: field,
          errors: [],
        })),
      );
    }
  };

  useEffect(() => {
    fectchEmissionControl();
    getFilterEmissionControl();
  }, []);

  useEffect(() => {
    fectchEmissionControl();
  }, [current, filter]);

  const columns: TableProps<EmissionControl>["columns"] = [
    {
      title: "No.",
      align: "center",
      render: (_, __, index) => <div>{index + 1 + (current - 1) * pageSize}</div>,
    },
    {
      title: "Emission Control",
      dataIndex: "emissionControl",
      filters: mapFilter("emissionControl"),
    },
    {
      title: "Efficiency (%)",
      dataIndex: "efficiency",
      filters: mapFilter("efficiency"),
      render: (_, record) => <div>{formatNumberWithFillZero(record.efficiency, 2)}</div>,
    },
    {
      title: "Remark",
      dataIndex: "remark",
    },
    {
      title: "Action",
      key: "action",
      render: (value, record) => (
        <Space>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => {
              onOpenModal("update", record);
            }}
            disabled={!canEditTable(permissions)}
          >
            <BsPencilSquare size={16} color={componentPalette.blueGrey} />
          </Button>
          <Button
            className="border-transparent bg-transparent"
            onClick={() => showDeleteConfirm(value)}
            disabled={!canEditTable(permissions)}
          >
            <BsTrashFill size={16} color={componentPalette.blueGrey} />
          </Button>
        </Space>
      ),
      align: "center",
    },
  ];
  return (
    <>
      {contextHolder}
      {form && (
        <EmissionControlModal
          form={form}
          open={showModal}
          mode={mode}
          loading={loading}
          isDuplicate={isDuplicate}
          onDuplicateChange={(isDup) => {
            setIsDuplicate(isDup);
          }}
          onCloseModal={onCloseModal}
          onSave={(mode, data) => onSave(mode, data)}
        />
      )}
      {canEditTable(permissions) && (
        <div style={{ display: "flex", justifyContent: "end", paddingBottom: "10px" }}>
          <Button
            type="primary"
            size="middle"
            onClick={() => onOpenModal("insert", initEmissionControl)}
            style={{
              display: "flex",
              alignItems: "center",
            }}
            disabled={!canEditTable(permissions)}
          >
            <PlusCircleOutlined />
            New
          </Button>
        </div>
      )}
      <Table
        className="w-full"
        columns={columns}
        dataSource={emissionControl}
        loading={loading}
        onChange={onTableChange}
        scroll={{ x: "max-content" }}
        pagination={{
          showSizeChanger: false,
          size: "small",
          current,
          pageSize,
          total: totalEmissionControl,
        }}
      />
    </>
  );
};

export default EmissionControlTable;
